/**
 * ===========================================
 * StudySpace ERP Frontend - Sessions API Service
 * ===========================================
 * 
 * Provides API functions for session check-in/check-out
 * and session listing. Integrates toast notifications
 * for user feedback on success or failure.
 * 
 * @file src/api/sessions.api.js
 * @description Sessions API service
 */

import api, { handleApiError } from './axios';

/**
 * Check in a user to a room (create session)
 * 
 * @param {Object} sessionData - Check-in data
 * @param {string} sessionData.user - User ID
 * @param {string} sessionData.room - Room ID
 * @param {string} sessionData.workspace - Workspace ID
 * @param {string} [sessionData.notes] - Optional notes
 * @returns {Promise<Object>} API response data
 */
export const checkIn = async (sessionData) => {
  try {
    const response = await api.post('/sessions/checkin', sessionData);
    return response.data;
  } catch (error) {
    const message = handleApiError(error);
    throw new Error(message);
  }
};

/**
 * Check out a user from a room (end session)
 * 
 * @param {string} sessionId - Session ID
 * @param {Object} [data={}] - Optional checkout data (notes)
 * @returns {Promise<Object>} API response data
 */
export const checkOut = async (sessionId, data = {}) => {
  try {
    const response = await api.post(`/sessions/${sessionId}/checkout`, data);
    return response.data;
  } catch (error) {
    const message = handleApiError(error);
    throw new Error(message);
  }
};

/**
 * List sessions with optional filters
 * 
 * @param {Object} [params={}] - Query parameters
 * @returns {Promise<Object>} API response data
 */
export const listSessions = async (params = {}) => {
  try {
    const response = await api.get('/sessions', { params });
    return response.data;
  } catch (error) {
    const message = handleApiError(error);
    throw new Error(message);
  }
};

/**
 * Get active sessions
 * 
 * If userId is provided, filters to that user's active sessions.
 * This uses the sessions list endpoint with status=active.
 * 
 * @param {string} [userId] - Optional user ID filter
 * @returns {Promise<Object>} API response data
 */
export const getActiveSession = async (userId) => {
  try {
    const params = { status: 'active' };
    if (userId) {
      // Backend expects 'user' query param for user filter
      params.user = userId;
    }
    const response = await api.get('/sessions', { params });
    return response.data;
  } catch (error) {
    const message = handleApiError(error);
    throw new Error(message);
  }
};

/**
 * Get all active sessions (staff view)
 * 
 * @param {Object} [params={}] - Optional filters (room, workspace)
 * @returns {Promise<Object>} API response data
 */
export const getActiveSessions = async (params = {}) => {
  try {
    const response = await api.get('/sessions/active', { params });
    return response.data;
  } catch (error) {
    const message = handleApiError(error);
    throw new Error(message);
  }
};

/**
 * Get session history for the logged-in member
 * 
 * @param {Object} [params={}] - Query parameters (status, startDate, endDate, page, limit)
 * @returns {Promise<Object>} API response data
 */
export const getMySessionHistory = async (params = {}) => {
  try {
    const response = await api.get('/sessions/my-history', { params });
    return response.data;
  } catch (error) {
    const message = handleApiError(error);
    throw new Error(message);
  }
};

export default {
  checkIn,
  checkOut,
  listSessions,
  getActiveSession,
  getActiveSessions,
  getMySessionHistory
};
